import React, { useEffect } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Country } from "country-state-city";
import { focusOnFirstError } from "./focusUtils";
import Select from "react-select";
import { ErrorNotification } from "../../tosterNotification/tosters";
import axios from "axios";
import * as pdfjsLib from "pdfjs-dist";

// Set the worker source for pdfjs-dist
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const apiKey = process.env.REACT_APP_OPENAI_API_KEY;

const PersonalInfoForm = ({
  formValues,
  setFormValues,
  formErrors,
  setFormErrors,
  inputRefs,
  customSelectStyles,
}) => {
  const countries = Country.getAllCountries();

  // Format countries for react-select
  const countryOptions = countries.map((country) => ({
    value: country.isoCode,
    label: country.name,
  }));

  // Format age groups for react-select
  const ageOptions = [
    "Below 18",
    "18 – 25",
    "26 – 34",
    "35 – 45",
    "46 – 55",
    "56 and above",
  ].map((age) => ({
    value: age,
    label: age,
  }));

  // Validate the form when CLIF checkbox changes
  useEffect(() => {
    if (formValues.clif_check) {
      validateFileUpload();
    } else {
      // Clear file upload error when checkbox is unchecked
      setFormErrors((prev) => ({
        ...prev,
        fileUpload: "",
      }));
    }
  }, [formValues.clif_check]);

  const validateFileUpload = () => {
    if (formValues.clif_check && !formValues.fileUpload) {
      setFormErrors((prev) => ({
        ...prev,
        fileUpload: "Resume is required for CLIF suggestion",
      }));
      return false;
    }
    return true;
  };

  const validateField = (name, value) => {
    const errors = {};
    switch (name) {
      case "firstname":
        if (!value) errors.firstname = "First name is required";
        break;
      case "lastname":
        if (!value) errors.lastname = "Last name is required";
        break;
      case "age":
        if (!value) errors.age = "Age group is required";
        break;
      case "country":
        if (!value) errors.country = "Country is required";
        break;
      case "job_goal":
        if (!formValues.clif_check) {
          if (!value) errors.job_goal = "Job goal is required";
          else if (value.trim().length < 50)
            errors.job_goal =
              "Dream Job Goal should be at least 50 characters long";
          else if (value.trim().length > 250)
            errors.job_goal =
              "Dream Job Goal should be at most 250 characters long";
        }
        break;
      case "fileUpload":
        if (!value) {
          errors.fileUpload = "Resume is required.";
        }
        if (formValues.clif_check && !value) {
          errors.fileUpload = "Resume is required for CLIF suggestion";
        }
        break;
      default:
        break;
    }
    return errors;
  };

  const handleChange = async (e) => {
    const { name, value, files, type, checked } = e.target;

    // Handle checkbox differently
    if (type === "checkbox") {
      const newCheckValue = checked;

      setFormValues((prev) => ({
        ...prev,
        [name]: newCheckValue,
        // If checkbox is checked, clear job_goal validation errors
        ...(name === "clif_check" && newCheckValue
          ? { job_goal: "", job_goal_error: "" }
          : { job_goal_error: "Job goal is required" }),
      }));

      // Validate resume upload if checkbox is checked
      if (name === "clif_check" && newCheckValue) {
        const fileUploadErrors = validateField(
          "fileUpload",
          formValues.fileUpload
        );
        setFormErrors((prev) => ({
          ...prev,
          ...fileUploadErrors,
        }));
      }

      return;
    }

    const newValue = files && files[0] ? files[0] : value;

    if (name === "fileUpload") {
      console.log("Selected file", files[0]);
      fileData(files[0]);
    }

    setFormValues((prev) => ({ ...prev, [name]: newValue }));
    const fieldErrors = validateField(name, newValue);
    setFormErrors((prev) => ({
      ...prev,
      [name]: fieldErrors[name] || "", // Clear error if valid
      ...(fieldErrors[name] ? fieldErrors : {}),
    }));

    if (Object.keys(fieldErrors).length > 0) {
      focusOnFirstError(fieldErrors, inputRefs);
    }
  };

  const extractTextFromPDF = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      let fullText = "";
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        const pageText = content.items.map((item) => item.str).join(" ");
        fullText += pageText + "\n";
      }

      console.log("Extracted PDF Text:", fullText);
      return fullText;
    } catch (error) {
      console.error("Error extracting text from PDF:", error);
      throw error;
    }
  };

  const fileData = async (file) => {
    try {
      const fileText = await extractTextFromPDF(file);

      const response = await axios.post(
        "https://api.openai.com/v1/chat/completions",
        {
          model: "gpt-3.5-turbo",
          messages: [
            {
              role: "user",
              content: `Analyze the following resume content and check if any of the following sections are missing:
                      - Work experience
                      - Projects completed
                      - Skills
                      - Certifications
                      - Languages known
                      - Interests/hobbies
                      - References
                      
                      If any are missing, respond ONLY with a **valid JSON array** of the missing sections like ["Work experience", "References"]. If none are missing, respond with an empty array [].
                      
              Resume:\n${fileText}`,
            },
          ],
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
          },
        }
      );

      const message = response.data.choices[0].message.content.trim();

      let missingSections = [];
      try {
        // Extract just the array from a JSON response even if some explanation text is added
        const arrayMatch = message.match(/\[.*?\]/s);
        if (arrayMatch) {
          missingSections = JSON.parse(arrayMatch[0]);
        } else {
          console.warn("No valid JSON array found in response:", message);
        }
      } catch (jsonError) {
        console.warn(
          "JSON parsing error from OpenAI response:",
          jsonError.message,
          message
        );
      }

      if (Array.isArray(missingSections) && missingSections.length > 0) {
        ErrorNotification(
          `Missing important resume sections: ${missingSections.join(", ")}`
        );
      }
    } catch (err) {
      console.error("Error analyzing resume:", err);
      ErrorNotification("Failed to analyze the resume. Please try again.");
    }
  };

  const handleSelectChange = (selectedOption, { name }) => {
    const value = selectedOption ? selectedOption.value : "";

    setFormValues((prev) => ({ ...prev, [name]: value }));
    const fieldErrors = validateField(name, value);
    setFormErrors((prev) => ({
      ...prev,
      [name]: fieldErrors[name] || "", // Clear error if valid
      ...(fieldErrors[name] ? fieldErrors : {}),
    }));

    if (name === "country" && value) {
      if (value !== "IN" && value !== "US") {
        ErrorNotification(
          "CLIF AI is only operating in India and USA for now. Please visit us after sometime"
        );
      }
      const country = countries.find((c) => c.isoCode === value);
      setFormValues((prev) => ({
        ...prev,
        country: value,
        phone_no: `+${country?.phonecode || ""}`, // Keep phone code as is
      }));
    }

    if (Object.keys(fieldErrors).length > 0) {
      focusOnFirstError(fieldErrors, inputRefs);
    }
  };

  return (
    <div className="form-section card" style={{ height: "365px" }}>
      <h5 className="mb-0">Personal Information</h5>
      <div className="row m-1 mb-0">
        <div className="col-lg-4">
          <div className="form-group">
            <label className="small-label fw-bold">First Name *</label>
            <input
              ref={inputRefs.firstname}
              type="text"
              className="form-control"
              placeholder="First Name"
              name="firstname"
              value={formValues.firstname || ""}
              onChange={handleChange}
              required
            />
            {formErrors.firstname && (
              <p className="text-red">{formErrors.firstname}</p>
            )}
          </div>
        </div>
        <div className="col-lg-4">
          <div className="form-group">
            <label className="small-label fw-bold">Last Name *</label>
            <input
              ref={inputRefs.lastname}
              type="text"
              className="form-control"
              placeholder="Last Name"
              name="lastname"
              value={formValues.lastname || ""}
              onChange={handleChange}
              required
            />
            {formErrors.lastname && (
              <p className="text-red">{formErrors.lastname}</p>
            )}
          </div>
        </div>
        <div className="col-lg-4">
          <div className="form-group">
            <label className="small-label fw-bold">
              Put a face to the name
            </label>
            <input
              ref={inputRefs.headshot}
              type="file"
              className="form-control"
              name="headshot"
              onChange={handleChange}
            />
          </div>
        </div>
        <div className="col-lg-4">
          <div className="form-group">
            <label className="small-label fw-bold">How young are you? *</label>
            <Select
              ref={inputRefs.age}
              name="age"
              options={ageOptions}
              onChange={(option) => handleSelectChange(option, { name: "age" })}
              value={
                ageOptions.find((option) => option.value === formValues.age) ||
                null
              }
              placeholder="Select Age Group"
              styles={customSelectStyles}
              classNamePrefix="select"
              className="form-control-select"
              isSearchable={true}
            />
            {formErrors.age && <p className="text-red">{formErrors.age}</p>}
          </div>
        </div>
        <div className="col-lg-4">
          <div className="form-group">
            <label className="small-label fw-bold">
              Tell us your country! *
            </label>
            <Select
              ref={inputRefs.country}
              name="country"
              options={countryOptions}
              onChange={(option) =>
                handleSelectChange(option, { name: "country" })
              }
              value={
                countryOptions.find(
                  (option) => option.value === formValues.country
                ) || null
              }
              placeholder="Select Country"
              styles={customSelectStyles}
              classNamePrefix="select"
              className="form-control-select"
            />
            {formErrors.country && (
              <p className="text-red">{formErrors.country}</p>
            )}
          </div>
        </div>
        <div className="col-lg-4">
          <div className="form-group">
            <label className="small-label fw-bold">Attach latest CV *</label>
            <input
              ref={inputRefs.fileUpload}
              type="file"
              name="fileUpload"
              accept=".pdf,.docx"
              className="form-control"
              onChange={handleChange}
              required
            />
            {formErrors.fileUpload && (
              <p className="text-red">{formErrors.fileUpload || ""}</p>
            )}
          </div>
        </div>
        <div className="col-lg-12 mt-n2">
          <div className="form-group">
            <label className="small-label fw-bold">
              {formValues.clif_check
                ? "Dream Job Goal (CLIF will suggest)"
                : "Dream Job Goal *"}
            </label>
            <textarea
              ref={inputRefs.job_goal}
              placeholder="I want to be a Project Manager at Google, USA, with a salary of $250,000 per year."
              className="form-control"
              rows={1}
              name="job_goal"
              value={formValues.job_goal || ""}
              onChange={handleChange}
              maxLength={250}
              minLength={50}
              disabled={formValues.clif_check}
              readOnly={formValues.clif_check}
              style={{
                height: "50px",
                minHeight: "50px",
                padding: "4px",
                minWidth: "500px",
                maxWidth: "100%",
                width: "100%",
              }}
            />
            {!formValues.clif_check && formErrors.job_goal && (
              <p className="text-red">{formErrors.job_goal}</p>
            )}
          </div>
        </div>
        <div className="form-group text-start mt-n2">
          <div className="form-check d-flex">
            <input
              type="checkbox"
              className="form-check-input border border-dark mt-0"
              ref={inputRefs.clif_check}
              id="clif_check"
              name="clif_check"
              checked={formValues.clif_check || false}
              onChange={handleChange}
            />
            <label className="form-check-label mt-0" htmlFor="clif_check">
              Not sure about your dream career? Let CLIF AI suggest a perfect
              career goal for you!
            </label>
          </div>
        </div>
      </div>
      <style jsx>{`
        .country-select-disabled {
          pointer-events: none;
          opacity: 0;
        }

        /* Override react-select to match form-control style */
        :global(.form-control-select) {
          padding: 0 !important;
          border: none !important;
        }

        :global(.form-control-select > div) {
          border-color: #ced4da !important;
        }

        :global(.form-control-select > div:focus-within) {
          border-color: #80bdff !important;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
        }

        /* Fix for the select components alignment */
        :global(.select__placeholder),
        :global(.select__single-value) {
          display: flex !important;
          align-items: center !important;
          height: 36px !important;
        }

        .form-section {
          overflow-y: auto;
          overflow-x: hidden;
        }

        @media (max-width: 992px) {
          .form-section {
            height: auto;
          }

          .form-group {
            margin-bottom: 1rem;
          }

          textarea {
            min-width: 100% !important;
          }
        }

        @media (max-width: 768px) {
          .form-group label {
            font-size: 0.875rem;
          }

          input.form-control,
          .form-control-select,
          textarea {
            font-size: 0.875rem;
          }

          .form-check-label {
            font-size: 0.85rem;
          }

          textarea {
            min-height: 70px !important;
          }

          .form-section {
            padding: 10px;
          }
        }

        @media (max-width: 576px) {
          .col-lg-4,
          .col-lg-12 {
            width: 100%;
            flex: 0 0 100%;
            max-width: 100%;
          }

          .form-control-select > div {
            height: auto !important;
          }

          textarea {
            width: 100% !important;
            min-width: 100% !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PersonalInfoForm;
